import { useState, useEffect, useCallback } from 'react'
import { SeasonAPI } from '../api/client'
import { useAuth } from '../store/auth'
import { useToast } from '../components/ui/Toast'
import SeasonXPBar from '../components/season/SeasonXPBar'
import BattlePassTrack from '../components/season/BattlePassTrack'
import PurchasePassModal from '../components/season/PurchasePassModal'

const ELEMENT_COLOR: Record<string, string> = {
  fire:  '#FF4800',
  water: '#00F0FF',
  earth: '#39FF14',
  air:   '#E0E0FF',
  void:  '#BF00FF',
  aether:'#FFD700',
}

type SeasonData = Awaited<ReturnType<typeof SeasonAPI.getCurrent>>['data'] & {}

export default function Season() {
  const { player, refreshPlayer } = useAuth()
  const { info, error: toastError } = useToast()
  const [data, setData] = useState<SeasonData | null>(null)
  const [loading, setLoading] = useState(true)
  const [showPurchase, setShowPurchase] = useState(false)
  const [purchasing, setPurchasing] = useState(false)
  const [claimedTiers, setClaimedTiers] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true)
    const res = await SeasonAPI.getCurrent()
    if (res.success && res.data) setData(res.data)
    else if (!res.success) toastError(res.error ?? 'Could not load season')
    setLoading(false)
  }, [toastError])

  useEffect(() => { load() }, [load])

  async function handlePurchasePass() {
    setPurchasing(true)
    const res = await SeasonAPI.purchasePass()
    setPurchasing(false)
    setShowPurchase(false)
    if (res.success) {
      info('Battle Pass unlocked! Premium rewards are now available.')
      refreshPlayer()
      load()
    } else {
      toastError(res.error ?? 'Purchase failed')
    }
  }

  async function handleClaim(tierId: string) {
    if (claimedTiers.has(tierId)) return
    const res = await SeasonAPI.claimReward(tierId)
    if (res.success && res.data) {
      setClaimedTiers(prev => new Set([...prev, tierId]))
      const { type, value } = res.data.reward
      info(`Claimed: ${type === 'atlas_coins' ? `${value} ⚡` : type === 'shards' ? `${value} 💎` : value ?? type ?? 'reward'}`)
      refreshPlayer()
    } else {
      toastError(res.error ?? 'Claim failed')
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <p className="text-ab-text-dim text-sm animate-pulse">Loading season…</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <p className="text-ab-text-dim text-sm">No active season.</p>
      </div>
    )
  }

  const { season, tiers, playerProgress, nextTierXP, xpToNextTier } = data
  const themeColor = ELEMENT_COLOR[season.theme_element] ?? '#FF4800'

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-label text-xs uppercase tracking-widest opacity-50 mb-1">Season {season.number}</p>
          <h1 className="font-display font-black text-3xl text-ab-text">{season.name}</h1>
          <p className="text-body text-sm opacity-50 mt-1">{season.daysRemaining} days remaining</p>
        </div>
        <div
          className="px-3 py-1.5 rounded-full font-display font-bold text-xs"
          style={{ background: `${themeColor}20`, color: themeColor, border: `1px solid ${themeColor}40` }}
        >
          {season.theme_element.toUpperCase()}
        </div>
      </div>

      {/* XP Progress */}
      <div className="card-base p-4">
        <SeasonXPBar
          seasonXP={playerProgress.seasonXP}
          currentTier={playerProgress.currentTier}
          nextTierXP={nextTierXP}
          xpToNextTier={xpToNextTier}
          themeElement={season.theme_element}
        />
        <div className="flex items-center justify-between mt-3">
          <span className="text-body text-xs opacity-50">
            Total: {playerProgress.seasonXP.toLocaleString()} Season XP
          </span>
          {!playerProgress.hasPremiumPass ? (
            <button
              onClick={() => setShowPurchase(true)}
              className="font-display font-bold text-xs px-3 py-1.5 rounded-full transition-all"
              style={{ background: `${themeColor}20`, color: themeColor, border: `1px solid ${themeColor}50` }}
            >
              Unlock Premium · {season.battle_pass_price_shards} 💎
            </button>
          ) : (
            <span className="text-xs font-display font-bold" style={{ color: themeColor }}>
              ✓ Premium Pass Active
            </span>
          )}
        </div>
      </div>

      {/* Battle Pass Track */}
      <div>
        <p className="text-label text-xs uppercase tracking-widest opacity-50 mb-3">Battle Pass — 50 Tiers</p>
        <BattlePassTrack
          tiers={tiers}
          currentTier={playerProgress.currentTier}
          hasPremiumPass={playerProgress.hasPremiumPass}
          themeColor={themeColor}
          onClaim={handleClaim}
        />
      </div>

      {/* Purchase Modal */}
      {showPurchase && (
        <PurchasePassModal
          seasonName={season.name}
          cost={season.battle_pass_price_shards}
          playerShards={player?.shards ?? 0}
          themeColor={themeColor}
          onConfirm={handlePurchasePass}
          onClose={() => setShowPurchase(false)}
          pending={purchasing}
        />
      )}
    </div>
  )
}
